import { useMutation, useQuery } from '@apollo/client';
import { getRemainingBalance } from '@nile/client_management/src/Utils/invoiceAndQuoteHelper';
import {
  AsyncPropSelector,
  Column,
  CurrencyText,
  CustomModal,
  getDateTimeFormat,
  getTranslatedText,
  preFormatForm,
  PSA_CONSTANTS,
  recordPaymentConstraints,
  Row,
  TEXT_LENGTH,
  useIsAuthorisedHook,
  validateForm,
  DateTimePicker,
  AccessControl,
  useActivityTracker,
  CurrencyField
} from '@nile/components';
import {
  Error,
  EventDispatcher,
  EventEmitter
} from '@nile/components/src/Utils/helpers';
import {
  AccountClient,
  GET_CLIENT_INVOICE_SETTINGS_NEW,
  GET_INVOICE_PAYMENT_METHODS
} from '@nile/graphql';
import { CREATE_PAYMENT } from '@nile/graphql/src/Mutation/partialPayments';
import { SVGIcon } from '@superops/super-icons';
import {
  Banner,
  Button,
  Card,
  DisplayText,
  Modal,
  Page,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import dayjs from 'dayjs';
import { isEmpty, pick } from 'lodash';
import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const RecordPaymentContext = createContext({});

export const useRecordPaymentContext = () => {
  const context = useContext(RecordPaymentContext);
  if (!context) {
    throw Error(
      'useRecordPaymentContext must be used within a RecordPaymentProvider'
    );
  }
  return context;
};

export function RecordPayment({
  children,
  invoiceTotal,
  hasCredits,
  clientId,
  skipFetchingInvoiceSettings,
  invoiceData
}) {
  const intl = useIntl();
  const [amount, setAmount] = useState('');
  const [dateOfPayment, setDateOfPayment] = useState(
    dayjs(new Date()).format('YYYY-MM-DD')
  );
  const [reference, setReference] = useState('');
  const [errors, setErrors] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);

  const isInvoiceSettingsViewAuthorised = useIsAuthorisedHook(
    'invoiceSettings.view'
  );

  const { loading: clientInvoiceSettingsLoading } = useQuery(
    GET_CLIENT_INVOICE_SETTINGS_NEW,
    {
      client: AccountClient,
      fetchPolicy: 'cache-and-network',
      skip:
        skipFetchingInvoiceSettings ||
        !clientId ||
        !isInvoiceSettingsViewAuthorised,
      variables: {
        input: {
          accountId: clientId
        }
      },
      onCompleted(response) {
        if (response?.getInvoiceSettings?.invoicePaymentMethod) {
          setPaymentMethod({
            ...response?.getInvoiceSettings.invoicePaymentMethod,
            label:
              response?.getInvoiceSettings.invoicePaymentMethod
                ?.paymentMethodName,
            value:
              response?.getInvoiceSettings.invoicePaymentMethod?.paymentMethodId
          });
        }
      }
    }
  );

  async function validate() {
    let validationResult = validateForm(
      preFormatForm({ amount, dateOfPayment }),
      recordPaymentConstraints
    );

    if (!validationResult?.amount && Number(amount) === 0) {
      if (!validationResult) {
        validationResult = {};
      }
      validationResult.amount = getTranslatedText(
        intl,
        'dromo.validation.requiredField'
      );
    }

    if (isEmpty(reference?.trim())) {
      if (!validationResult) {
        validationResult = {};
      }
      validationResult.reference = getTranslatedText(
        intl,
        'dromo.validation.requiredField'
      );
    }

    if (isEmpty(paymentMethod?.value)) {
      if (!validationResult) {
        validationResult = {};
      }
      validationResult.paymentMethod = getTranslatedText(
        intl,
        'dromo.validation.requiredField'
      );
    }

    if (validationResult) {
      setErrors(validationResult);
      return { isValid: false };
    }

    setErrors(null);
    return {
      isValid: true,
      formData: { amount, dateOfPayment, paymentMethod, reference }
    };
  }

  return (
    <RecordPaymentContext.Provider
      value={{
        amount,
        setAmount,
        dateOfPayment,
        setDateOfPayment,
        reference,
        setReference,
        errors,
        paymentMethod,
        setPaymentMethod,
        validate,
        invoiceTotal,
        hasCredits,
        totalAmountPaid: invoiceData?.amountPaid,
        invoiceData
      }}
    >
      {children}
    </RecordPaymentContext.Provider>
  );
}

export function WarningRecordPayment({ primaryAction, secondaryAction }) {
  const intl = useIntl();

  return (
    <Modal
      title={getTranslatedText(intl, 'common.warning')}
      open
      iconSize="xl"
      variant="confirmation"
      onClose={secondaryAction}
      primaryAction={{
        content: getTranslatedText(intl, 'common.ok'),
        onAction: primaryAction
      }}
      secondaryActions={[
        {
          content: getTranslatedText(intl, 'common.cancel'),
          onAction: secondaryAction
        }
      ]}
    >
      <div className="pt10">
        {getTranslatedText(
          intl,
          'invoice.partialPayments.excessPaymentWarning',
          {
            br: <br />,
            ul: (chunk) => <ul>{chunk}</ul>,
            li: (chunk) => <li className="mb10">{chunk}</li>
          }
        )}
      </div>
    </Modal>
  );
}

RecordPayment.Form = function RecordPaymentForm() {
  const intl = useIntl();

  const {
    amount,
    setAmount,
    dateOfPayment,
    setDateOfPayment,
    reference,
    setReference,
    errors,
    paymentMethod,
    setPaymentMethod,
    invoiceTotal,
    invoiceData
  } = useRecordPaymentContext();

  useEffect(() => {
    setAmount(String(invoiceData?.amountDue ?? invoiceTotal));
  }, [invoiceTotal, invoiceData?.amountDue]);

  const { dateFormat } = getDateTimeFormat();

  const updatePaymentMethod = (field, option) => {
    setPaymentMethod({
      ...option,
      value: option.paymentMethodId,
      label: option.paymentMethodName
    });
  };

  return (
    <Column gap={20} className="w50">
      <Column gap={5}>
        <TextStyle weight="Regular">
          <FormattedMessage id="invoice.partialPayment.amountReceived" />
        </TextStyle>
        <CurrencyField
          value={amount}
          onChange={(val) => {
            console.log('val', val);
            setAmount(val);
          }}
          placeholder={getTranslatedText(intl, 'common.enterAmount')}
          error={errors?.amount}
          fontSize="fontLarge"
          prefix={<CurrencyText symbolOnly />}
        />
      </Column>
      <Column gap={5}>
        <TextStyle weight="Regular">
          <FormattedMessage id="invoice.partialPayments.dateOfPayment" />
        </TextStyle>
        <Column gap={5}>
          <Row gap={5} className="items-center extend-datepicker">
            <SVGIcon name="DatePicker" />
            <DateTimePicker
              selected={dateOfPayment && dayjs(dateOfPayment).toDate()}
              onChange={(date) => {
                setDateOfPayment(dayjs(date).format('YYYY-MM-DD'));
              }}
              placeholderText={getTranslatedText(
                intl,
                'invoice.partialPayments.placeholder'
              )}
              dateFormat={dateFormat}
              required
              disabled={false}
              showTimeSelect={false}
              minDate={null}
              popperPlacement="auto"
            />
          </Row>
          <Error>{errors?.dateOfPayment}</Error>
        </Column>
      </Column>
      <Column gap={5}>
        <TextStyle weight="Regular">
          <FormattedMessage id="invoice.partialPayment.paymentMethod" />
        </TextStyle>
        <Column gap={5}>
          <Row gap={5} className="items-center">
            <SVGIcon name="BillingAndInvoice" />
            <AsyncPropSelector
              key={paymentMethod?.value || 'empty'}
              autoFocus
              hideTitle
              isDynamicQuery
              variables={null}
              defaultValue={paymentMethod}
              updateSelection={updatePaymentMethod}
              generateOptions={(field, data) => {
                const optionsArray = data?.[field]?.map((option) => ({
                  ...option,
                  value: option.paymentMethodId,
                  label: option.paymentMethodName
                }));
                return optionsArray;
              }}
              entity="getPaymentMethods"
              field="getPaymentMethods"
              onKeyDown={updatePaymentMethod}
              dynamicQuery={() => {
                return AccountClient.query({
                  query: GET_INVOICE_PAYMENT_METHODS
                });
              }}
              placeholder={getTranslatedText(
                intl,
                'invoice.partialPayments.paymentMethodPlaceholder'
              )}
              error={errors?.paymentMethod}
              showErrorMessage={false}
            />
          </Row>
          <Error>{errors?.paymentMethod}</Error>
        </Column>
      </Column>
      <Column gap={5}>
        <TextStyle weight="Regular">
          <FormattedMessage id="invoice.partialPayments.reference" />
        </TextStyle>
        <TextField
          prefix={<SVGIcon name="Notes" />}
          type="text"
          value={reference}
          onChange={setReference}
          placeholder={getTranslatedText(intl, 'common.enter')}
          maxLength={TEXT_LENGTH.size_5}
          error={errors?.reference}
        />
      </Column>
    </Column>
  );
};

RecordPayment.Breakdown = function PaymentBreakdown() {
  const intl = useIntl();

  const {
    amount: amountPaid,
    invoiceTotal,
    totalAmountPaid
  } = useRecordPaymentContext();

  const {
    remainingBalance,
    isExcess,
    remainingAmountStatus,
    amountPaidAfterBreakdown
  } = getRemainingBalance({
    invoiceTotal,
    amountPaid,
    totalAmountPaid
  });

  if (
    remainingAmountStatus === PSA_CONSTANTS.NEUTRAL ||
    !amountPaid ||
    Number(amountPaid) === 0
  )
    return null;

  return (
    <Column gap={10} className="grow-1">
      {isExcess && (
        <Banner status="purpleInfo" hideIcon>
          <Row gap={10} className="items-center">
            <SVGIcon name="Wallet" />
            <FormattedMessage
              id="invoice.partialPayments.excessAmountBanner"
              values={{
                b: (chunk) => (
                  <DisplayText element="span" size="large" weight="Bold">
                    {chunk}
                  </DisplayText>
                )
              }}
            >
              {(chunk) => <span>{chunk}</span>}
            </FormattedMessage>
          </Row>
        </Banner>
      )}
      <Card padding="sizeExtraSmall" variant="secondaryCard" border="secondary">
        <Column>
          <div className="mb10">
            <DisplayText element="div" size="extraLarge" weight="SemiBold">
              {getTranslatedText(
                intl,
                'invoice.partialPayments.paymentBreakdown'
              )}
            </DisplayText>
          </div>
          <Column gap={15}>
            <Row className="space-between">
              <DisplayText element="div" size="large" weight="Regular">
                <FormattedMessage id="invoice.partialPayments.invoiceTotal" />
              </DisplayText>
              <DisplayText element="div" size="extraLarge" weight="Medium">
                <CurrencyText value={invoiceTotal} />
              </DisplayText>
            </Row>
            <Row className="space-between">
              <DisplayText element="div" size="large" weight="Regular">
                <FormattedMessage id="invoice.partialPayments.amountPaid" />
              </DisplayText>
              <DisplayText element="div" size="extraLarge" weight="Medium">
                <CurrencyText value={amountPaidAfterBreakdown} />
              </DisplayText>
            </Row>
            <Row className="border-top-grey py15 space-between">
              <DisplayText element="div" size="large" weight="Regular">
                <FormattedMessage
                  id={
                    isExcess
                      ? 'invoice.partialPayments.excessAmount'
                      : 'invoice.partialPayments.balanceDue'
                  }
                />
              </DisplayText>
              <div className="f-16">
                <TextStyle variation={isExcess ? 'positive' : 'negative'}>
                  <CurrencyText value={remainingBalance} />
                </TextStyle>
              </div>
            </Row>
          </Column>
        </Column>
      </Card>
    </Column>
  );
};

function RecordPaymentModalBase({ invoiceId, isModalActive, toggleModal }) {
  const {
    hasCredits,
    validate,
    amount,
    dateOfPayment,
    paymentMethod,
    reference,
    setAmount,
    setDateOfPayment,
    setReference,
    setPaymentMethod,
    invoiceTotal
  } = useRecordPaymentContext();

  const [showWarning, setShowWarning] = useState(false);

  const [createPayment, { loading: createPaymentLoading }] = useMutation(
    CREATE_PAYMENT,
    {
      client: AccountClient
    }
  );

  const activator = (
    <AccessControl
      roleToCheck="invoice.add"
      render={
        <Button
          onClick={() => {
            toggleModal();
            useActivityTracker({
              event: 'INVOICE_RECORD_PAYMENT_CLICKED'
            });
          }}
          icon={<SVGIcon name="Billable" />}
          primary
        >
          <FormattedMessage id="invoice.partialPayments.recordPayment" />
        </Button>
      }
    />
  );

  function createPaymentMutation() {
    createPayment({
      variables: {
        input: {
          amount,
          paymentDate: dateOfPayment,
          paymentMethod: pick(paymentMethod, ['paymentMethodId']),
          paymentReference: reference,
          invoice: {
            invoiceId
          }
        }
      }
    })
      .then(() => {
        EventDispatcher.dispatch('refetchInvoice');
        EventEmitter.dispatch('refetchClassicViewList');
      })
      .finally(() => {
        toggleModal();
      });
  }

  async function onRecordPayment() {
    const { isValid, formData } = await validate();

    if (!isValid) {
      return;
    }

    const { isExcess } = getRemainingBalance({
      invoiceTotal,
      amountPaid: formData?.amount
    });

    if (isExcess) {
      setShowWarning(true);
    } else {
      createPaymentMutation();
    }
  }

  return (
    <>
      {activator}
      {showWarning && (
        <WarningRecordPayment
          primaryAction={() => {
            setShowWarning(false);
            createPaymentMutation();
          }}
          secondaryAction={() => {
            setShowWarning(false);
          }}
        />
      )}

      {isModalActive && (
        <CustomModal open onClose={toggleModal} zIndex={1}>
          <div className="w900">
            <Page
              variant="primary"
              contentHeight="120px"
              title={
                <TextStyle weight="Bold">
                  <FormattedMessage id="invoice.partialPayments.recordPayment" />
                </TextStyle>
              }
              primaryAction={
                <Row gap={20}>
                  <Button primary onClick={toggleModal} plainSecondary>
                    <FormattedMessage id="common.cancel" />
                  </Button>
                  <Button
                    primary
                    onClick={onRecordPayment}
                    icon={<SVGIcon name="Billable" />}
                    loading={createPaymentLoading}
                  >
                    <FormattedMessage id="common.save" />
                  </Button>
                </Row>
              }
            >
              <Row gap={30}>
                <RecordPayment.Form />
                <RecordPayment.Breakdown />
              </Row>
            </Page>
          </div>
        </CustomModal>
      )}
    </>
  );
}

export function RecordPaymentModal(props) {
  const [isModalActive, toggleModal] = useReducer((s) => !s, false);

  return (
    <RecordPayment {...props} key={isModalActive ? 1 : 2}>
      <RecordPaymentModalBase
        invoiceId={props.invoiceId}
        isModalActive={isModalActive}
        toggleModal={toggleModal}
      />
    </RecordPayment>
  );
}
